import axios from "axios";
import { BASE_NODE_URL, PRODUCTS_LIST_NODE, PRODUCTS_LIST_WITH_TRANSFERS_NODE } from "../../Constants/Config";
import { normalizeTransferLoginEmployeeId } from "./transferAuth";

/** True when axios aborted the request (AbortController signal). */
export function isTransferCatalogAbortError(err) {
  return (
    err?.code === "ERR_CANCELED" ||
    err?.name === "CanceledError" ||
    err?.name === "AbortError" ||
    axios.isCancel?.(err) === true
  );
}

/**
 * Unwrap common API shapes: raw array, { data }, { result }, { products }, { items }.
 */
export function extractProductsArray(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;

  const dataBlock =
    payload.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : null;

  const inner =
    dataBlock?.products ??
    dataBlock?.items ??
    dataBlock?.rows ??
    dataBlock?.list ??
    payload.data ??
    payload.result ??
    payload.products ??
    payload.items ??
    payload.rows ??
    payload.list;

  if (Array.isArray(inner)) return inner;
  if (inner && !Array.isArray(inner) && typeof inner === "object") {
    if (Array.isArray(inner.data)) return inner.data;
    if (Array.isArray(inner.rows)) return inner.rows;
    if (Array.isArray(inner.list)) return inner.list;
    if (Array.isArray(inner.products)) return inner.products;
  }
  return [];
}

/** Normalize API pagination (string or number fields). */
export function extractPagination(payload) {
  const dataBlock =
    payload?.data && typeof payload.data === "object" && !Array.isArray(payload.data)
      ? payload.data
      : null;
  const root =
    payload?.pagination ??
    dataBlock?.pagination ??
    dataBlock;
  if (!root || typeof root !== "object") return null;
  const limit = Number(root.limit);
  const offset = Number(root.offset);
  const total = Number(root.total);
  return {
    limit: Number.isFinite(limit) ? limit : 20,
    offset: Number.isFinite(offset) ? offset : 0,
    total: Number.isFinite(total) ? total : 0,
  };
}

function numStock(v) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

function extractCustomCodes(raw) {
  if (!raw || typeof raw !== "object") return [];
  const codes = raw.custom_codes ?? raw.custom_code ?? raw.customCodes;
  if (Array.isArray(codes)) {
    return codes.map((c) => String(c).trim()).filter(Boolean);
  }
  if (codes != null && String(codes).trim() !== "") {
    return [String(codes).trim()];
  }
  return [];
}

/**
 * Transfer catalog on-hand / transferable qty.
 * Prefer transfer_availability (scalar or object), then quantity; else product stock fields.
 */
function resolveTransferRemainingQty(raw) {
  if (!raw || typeof raw !== "object") return 0;

  const availability = raw.transfer_availability ?? raw.transferAvailability;
  if (availability != null && availability !== "") {
    if (typeof availability !== "object") {
      return numStock(availability);
    }
    const fromAvailability = numStock(
      availability.remaining_qty ??
      availability.quantity ??
      availability.send_qty,
    );
    if (
      availability.remaining_qty != null ||
      availability.quantity != null ||
      availability.send_qty != null
    ) {
      return fromAvailability;
    }
  }

  return numStock(
    raw.quantity ??
    raw.stock ??
    raw.inventory ??
    raw.qty ??
    raw.on_hand ??
    raw.count ??
    0,
  );
}

/**
 * Map one API product record → shape expected by NewTransferPage catalog UI.
 */
export function mapApiProductToTransferItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = String(raw.id ?? raw.product_id ?? raw.item_id ?? "").trim();
  if (!id) return null;

  const name = raw.name ?? raw.title ?? raw.product_name ?? "Untitled product";
  const sku = String(raw.sku ?? raw.upc_code ?? raw.upc ?? raw.product_sku ?? "").trim();
  const upc = String(raw.upc ?? raw.upc_code ?? sku ?? "").trim();
  const customCodes = extractCustomCodes(raw);
  const category = String(
    raw.category_name ?? raw.category ?? raw.department ?? "—",
  );

  const variantSources =
    raw.variants ??
    raw.variant ??
    raw.varients ??
    raw.product_variants ??
    [];

  const list = Array.isArray(variantSources) ? variantSources : [];
  const variants = list
    .map((v, i) => {
      if (!v || typeof v !== "object") return null;
      const vid = String(
        v.id ?? v.variant_id ?? v.variation_id ?? `${id}-v-${i}`,
      );
      const vsku = String(v.sku ?? v.upc ?? v.variant_sku ?? "").trim();
      const vupc = String(v.upc ?? v.upc_code ?? vsku ?? "").trim();
      const vCustomCodes = extractCustomCodes(v);
      const label = String(
        v.label ??
        v.variant_name ??
        v.option_title ??
        v.option_values ??
        v.title ??
        v.name ??
        v.display_name ??
        v.variant ??
        (vsku || `Variant ${i + 1}`),
      );
      const onHand = resolveTransferRemainingQty(v);
      return {
        ...v,
        id: vid,
        sku: vsku,
        upc: vupc,
        customCodes: vCustomCodes,
        label,
        onHand,
      };
    })
    .filter(Boolean);

  const hasVariants = variants.length > 0;
  const onHandSimple = resolveTransferRemainingQty(raw);
  const onHand = hasVariants
    ? variants.reduce((s, v) => s + v.onHand, 0)
    : onHandSimple;

  return {
    id,
    name: String(name),
    sku,
    upc,
    customCodes,
    category,
    variantCount: hasVariants ? variants.length : null,
    onHand,
    hasVariants,
    ...(hasVariants ? { variants } : {}),
  };
}

/**
 * POST {BASE_NODE_URL}{PRODUCTS_LIST_NODE} — product list for transfer picker.
 * Body: merchant auth + `search` + pagination + sort.
 */
export async function postTransferProductCatalog({
  token,
  merchant_id,
  token_id,
  login_type,
  session_id,
  login_employee_id = null,
  search = "",
  limit = 20,
  offset = 0,
  filter_sort_by = 1,
  filter_order_by = "DESC",
  signal,
} = {}) {
  if (!token) {
    throw new Error("Not authenticated");
  }
  // const url = `${BASE_NODE_URL}${PRODUCTS_LIST_NODE}`;
  const url = `${BASE_NODE_URL}${PRODUCTS_LIST_WITH_TRANSFERS_NODE}`;
  const body = {
    merchant_id: String(merchant_id ?? ""),
    token_id: Number.isFinite(Number(token_id)) ? Number(token_id) : token_id,
    login_type: login_type ?? "merchant",
    session_id: Number.isFinite(Number(session_id))
      ? Number(session_id)
      : session_id,
    login_employee_id: normalizeTransferLoginEmployeeId(
      login_employee_id,
      login_type,
    ),
    search: search ?? "",
    limit: Number(limit) || 20,
    offset: Number(offset) || 0,
    filter_sort_by: Number(filter_sort_by) || 1,
    filter_order_by: filter_order_by ?? "DESC",
  };
  const response = await axios.post(url, body, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    signal,
  });

  const payload = response?.data;
  if (payload?.status === false) {
    const msg = payload?.message || payload?.error || "Could not load products.";
    throw new Error(typeof msg === "string" ? msg : "Could not load products.");
  }

  const rows = extractProductsArray(payload);
  const items = rows.map(mapApiProductToTransferItem).filter(Boolean);
  const pagination =
    extractPagination(response.data) || {
      limit: body.limit,
      offset: body.offset,
      total: items.length,
    };

  return { items, pagination };
}

function normalizeScanCode(code) {
  return String(code ?? "").trim().toLowerCase();
}

function collectCatalogCodes(entity) {
  const codes = new Set();
  const add = (value) => {
    const normalized = normalizeScanCode(value);
    if (normalized) codes.add(normalized);
  };
  if (!entity || typeof entity !== "object") return codes;
  add(entity.sku);
  add(entity.upc);
  add(entity.upc_code);
  add(entity.product_sku);
  if (Array.isArray(entity.customCodes)) {
    entity.customCodes.forEach(add);
  }
  return codes;
}

function catalogCodesInclude(entity, code) {
  const needle = normalizeScanCode(code);
  if (!needle) return false;
  return collectCatalogCodes(entity).has(needle);
}

/**
 * Build transfer cart target from a mapped transfer-list catalog item.
 */
export function buildTransferCartTargetFromCatalogItem(product, variant = null) {
  if (!product?.id) return null;

  const productId = String(product.id);
  const variantId = variant?.id != null ? String(variant.id) : "";
  const key = variantId ? `${productId}:${variantId}` : `${productId}:simple`;
  const max = variant ? Number(variant.onHand) || 0 : Number(product.onHand) || 0;
  const meta = { p: product, v: variant ?? null };

  return {
    key,
    max,
    meta,
    product,
  };
}

/**
 * Find an exact SKU/UPC/custom-code match within transfer-list search results.
 */
export function findExactTransferCatalogScanMatch(items, code) {
  if (!Array.isArray(items) || !items.length) return null;
  const needle = normalizeScanCode(code);
  if (!needle) return null;

  for (const product of items) {
    if (product?.hasVariants && Array.isArray(product.variants)) {
      for (const variant of product.variants) {
        if (catalogCodesInclude(variant, needle)) {
          return buildTransferCartTargetFromCatalogItem(product, variant);
        }
      }
    }
  }

  for (const product of items) {
    if (!product?.hasVariants && catalogCodesInclude(product, needle)) {
      return buildTransferCartTargetFromCatalogItem(product, null);
    }
  }

  for (const product of items) {
    if (
      product?.hasVariants &&
      Array.isArray(product.variants) &&
      product.variants.length === 1 &&
      catalogCodesInclude(product, needle)
    ) {
      return buildTransferCartTargetFromCatalogItem(product, product.variants[0]);
    }
  }

  return null;
}

/**
 * Scan lookup via POST products/transfer-list (same API as the catalog picker).
 */
export async function lookupTransferProductByScan({
  token,
  merchant_id,
  token_id,
  login_type,
  session_id,
  login_employee_id = null,
  code,
  signal,
} = {}) {
  const trimmed = String(code ?? "").trim();
  if (!token || !merchant_id || !trimmed) return null;

  const { items } = await postTransferProductCatalog({
    token,
    merchant_id,
    token_id,
    login_type,
    session_id,
    login_employee_id,
    search: trimmed,
    limit: 50,
    offset: 0,
    filter_sort_by: 1,
    filter_order_by: "DESC",
    signal,
  });

  return findExactTransferCatalogScanMatch(items, trimmed);
}
